/** PUBLIC CONSULTING QUOTE — /q/:token. Client-facing, Kindra-premium.
 * Benchmark: the Discover-flow proposal page + Built by Kindra.
 * Vision section above line items; accept = name + tick + timestamp. */
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import V2Logo from './V2Logo';

const API = process.env.REACT_APP_BACKEND_URL;
const C = {
  navy: '#100838', surface: 'rgba(220,240,245,0.04)',
  hair: 'rgba(220,240,245,0.10)', hairBright: 'rgba(220,240,245,0.20)',
  icy: '#DCF0F5', muted: 'rgba(220,240,245,0.62)', faint: 'rgba(220,240,245,0.42)',
  spectrum: 'linear-gradient(95deg, #34D1B0 0%, #1FB6C1 25%, #4F8FD0 50%, #9156C4 75%, #EB258D 100%)',
};
const ACCENTS = ['#34D1B0', '#1FB6C1', '#4F8FD0', '#9156C4', '#EB258D'];
const money = (v) => `$${Number(v || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const eyebrow = { fontFamily: 'Inter', fontSize: 12, letterSpacing: '0.18em', textTransform: 'uppercase', color: C.faint, fontWeight: 600 };

const PageStyle = () => (
  <style>{`
    @keyframes cqp-fade-up { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
    .cqp-fade { animation: cqp-fade-up 560ms cubic-bezier(0.22,1,0.36,1) both; }
    .cqp-d1 { animation-delay: 80ms; } .cqp-d2 { animation-delay: 160ms; } .cqp-d3 { animation-delay: 240ms; } .cqp-d4 { animation-delay: 320ms; }
    .cqp-img-card { transition: transform 220ms cubic-bezier(0.22,1,0.36,1), box-shadow 220ms ease, border-color 220ms ease; }
    .cqp-img-card:hover { transform: translateY(-3px); box-shadow: 0 22px 60px rgba(0,0,0,0.5); border-color: ${C.hairBright} !important; }
    .cqp-cta:hover { transform: translateY(-2px); box-shadow: 0 16px 44px rgba(145,86,196,0.45) !important; }
    .cqp-cta { transition: transform 200ms ease, box-shadow 200ms ease; }
    .cqp-accept:not(:disabled):hover { transform: translateY(-2px); box-shadow: 0 16px 44px rgba(235,37,141,0.4) !important; }
    .cqp-accept { transition: transform 200ms ease, box-shadow 200ms ease, background 200ms ease; }
    .cqp-undo-pill:not(:disabled):hover { border-color: rgba(220,240,245,0.5) !important; background: rgba(220,240,245,0.08) !important; transform: translateY(-1px); }
    .cqp-undo-pill { transition: border-color 200ms ease, background 200ms ease, transform 200ms ease; }
    @keyframes cqp-pulse-count { 0%, 100% { opacity: 1; } 50% { opacity: 0.55; } }
    .cqp-undo-count { animation: cqp-pulse-count 1s ease-in-out infinite; }
    @media (prefers-reduced-motion: reduce) { .cqp-fade { animation: none; } .cqp-undo-count { animation: none; } }
  `}</style>
);

export default function V2CQPublicPage() {
  const { token } = useParams();
  const [q, setQ] = useState(null);
  const [err, setErr] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [name, setName] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [accepted, setAccepted] = useState(false);
  // Client-side selection for the commercial-option toggles. Defaults to
  // standard/standard; total recalculates live as they change.
  const [tier, setTier] = useState('standard');       // 'standard' | 'priority'
  const [payment, setPayment] = useState('standard'); // 'standard' | 'payinfull'
  // 60-second undo state
  const [pendingUntil, setPendingUntil] = useState(null);   // ISO string
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [undoBusy, setUndoBusy] = useState(false);
  const [undoDone, setUndoDone] = useState(false);          // 'reverted' state

  useEffect(() => {
    fetch(`${API}/api/cq/${token}`).then(async (r) => {
      if (!r.ok) throw new Error();
      const d = await r.json();
      setQ(d);
      if (d.status === 'accepted') {
        setAccepted(true);
      } else if (d.status === 'accepted_pending' && d.pending_finalize_at) {
        // A returning visitor still inside the undo window
        setAccepted(true);
        setPendingUntil(d.pending_finalize_at);
      }
    }).catch(() => setErr(true));
  }, [token]);

  // Countdown tick — decrements once per second while a pending window is open
  useEffect(() => {
    if (!pendingUntil) { setSecondsLeft(0); return; }
    const tick = () => {
      const ms = new Date(pendingUntil).getTime() - Date.now();
      const s = Math.max(0, Math.ceil(ms / 1000));
      setSecondsLeft(s);
      if (s <= 0) setPendingUntil(null);   // window closed; pill disappears, acceptance is final
    };
    tick();
    const int = setInterval(tick, 1000);
    return () => clearInterval(int);
  }, [pendingUntil]);

  const track = (kind) => fetch(`${API}/api/cq/${token}/vision_event`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind }) }).catch(() => {});
  const openLightbox = (img) => { setLightbox(img); track('lightbox'); };
  const accept = async () => {
    if (!name.trim() || !agree) return;
    setBusy(true);
    try {
      const r = await fetch(`${API}/api/cq/${token}/accept`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: name.trim(), agree: true, tier, payment })
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setAccepted(true);
      // If backend returned an undo window, arm the countdown
      if (d.pending && d.undo_until) setPendingUntil(d.undo_until);
    } catch { alert('Something went wrong — email michael@kindraai.dev'); }
    setBusy(false);
  };
  const undoAccept = async () => {
    if (undoBusy || !pendingUntil) return;
    setUndoBusy(true);
    try {
      const r = await fetch(`${API}/api/cq/${token}/undo_accept`, { method: 'POST' });
      if (!r.ok) throw new Error();
      // Reset to the pre-accept state cleanly
      setAccepted(false);
      setPendingUntil(null);
      setUndoDone(true);
      setName('');
      setAgree(false);
      // Auto-clear the "reverted" hint after a moment so the accept box just looks normal
      setTimeout(() => setUndoDone(false), 6000);
    } catch { alert('Undo failed — email michael@kindraai.dev'); }
    setUndoBusy(false);
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen" style={{ minHeight: '100vh', background: C.navy, color: C.icy }}>
      <PageStyle />
      <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(900px 600px at -10% -10%, rgba(235,37,141,0.10), transparent 60%),'
                  + 'radial-gradient(900px 600px at 110% 10%, rgba(52,209,176,0.10), transparent 60%),'
                  + 'radial-gradient(700px 500px at 50% 110%, rgba(79,143,208,0.08), transparent 65%)' }} />
      <main style={{ position: 'relative', zIndex: 1, maxWidth: 760, margin: '0 auto', padding: '44px 20px 70px' }}>{children}</main>
    </div>
  );

  if (err) return <Shell><div style={{ textAlign: 'center', marginTop: 90 }}><V2Logo variant="main" tone="icy" height={28} /><p style={{ color: C.muted, fontFamily: 'Inter', fontSize: 16, marginTop: 24 }}>This quote link isn't available. Email <a href="mailto:michael@kindraai.dev" style={{ color: '#34D1B0' }}>michael@kindraai.dev</a></p></div></Shell>;
  if (!q) return <Shell><p style={{ color: C.faint, fontFamily: 'Inter', textAlign: 'center', marginTop: 90 }}>Loading…</p></Shell>;

  const t = q.totals || {};
  const expired = q.status === 'expired';
  const vu = q.valid_until ? new Date(q.valid_until).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
  const hasVision = (q.vision?.images || []).length > 0 || q.vision?.link_url;

  // ── Commercial options — live-updating totals ────────────────────────
  //
  // Rules the client hosted page enforces (so we NEVER show a broken "$0"
  // option to Damien / Daniel / any client):
  //   • Priority upgrade card is rendered ONLY when MJ enabled it AND the
  //     addon amount is > 0 (either $ price set, or % of total > 0). If the
  //     toggle is on but no amount is set, the card is HIDDEN entirely.
  //   • Pay-in-full card is rendered ONLY when MJ enabled it AND a discount
  //     percent > 0 is set. If the toggle is on but the percent is
  //     null/0/empty, the card is HIDDEN entirely.
  //   • The addon $ and the "you save $" shown ON the cards are the
  //     *available* figures (visible even before clicking). Only the TOTAL
  //     changes on click — the card values are always the true numbers.
  //
  const co = q.commercial_options || {};
  const baseTotal = Number(t.total_inc) || 0;

  // Server-computed available addon (already resolves $ vs %). Falls back
  // to a client-side compute if the server field is missing/stale, so a
  // fresh Draft doesn't render "+$0" before the server has re-echoed.
  const priorityAddonAvailable = (() => {
    const fromServer = Number(q.commercial_computed?.priority_addon);
    if (fromServer > 0) return fromServer;
    if (!co.priority_enabled) return 0;
    const p$ = Number(co.priority_price_aud);
    if (p$ > 0) return p$;
    const pPct = Number(co.priority_percent);
    if (pPct > 0) return Math.round(baseTotal * pPct / 100 * 100) / 100;
    return 0;
  })();

  // Available pay-in-full savings: percent × (base + priority-if-selected).
  // Rendered on the card as "You save $X" regardless of selection.
  const pifPct = Number(co.payinfull_discount_percent);
  const pifPctValid = co.payinfull_enabled && pifPct > 0;

  // Rendering flags — never render a card whose amount would be $0.
  const showPriorityCard = !!co.priority_enabled && priorityAddonAvailable > 0;
  const showPayInFullCard = pifPctValid;

  // Only reveal the whole "Build your deal" section when at least one
  // real option (or an informational delivery-weeks seal) is meaningful.
  const hasCO = !!(showPriorityCard || showPayInFullCard || co.delivery_weeks);

  // Applied amounts (drive the total). Only kick in when the user has
  // *clicked* the card AND the card is actually being rendered.
  const priorityAmt = (tier === 'priority' && showPriorityCard) ? priorityAddonAvailable : 0;
  const subForPif = baseTotal + priorityAmt;

  // "Available" savings shown on the pay-in-full card even before it's
  // selected — same math the total uses once selected.
  const payinfullSavingsAvailable = showPayInFullCard
    ? Math.round(subForPif * pifPct / 100 * 100) / 100
    : 0;

  const discountAmt = (payment === 'payinfull' && showPayInFullCard) ? payinfullSavingsAvailable : 0;
  const finalTotal = Math.max(0, subForPif - discountAmt);

  return (
    <Shell>
      {lightbox && (
        <div data-testid="cqp-lightbox" onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, zIndex: 100, background: 'rgba(8,4,26,0.95)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18, cursor: 'zoom-out', backdropFilter: 'blur(8px)' }}>
          <img src={lightbox} alt="Concept mockup" style={{ maxWidth: '96vw', maxHeight: '92vh', borderRadius: 16, boxShadow: '0 40px 110px rgba(0,0,0,0.75)' }} />
        </div>
      )}

      {/* Brand mark */}
      <div className="cqp-fade" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 44 }}>
        <V2Logo variant="main" tone="icy" height={28} />
        <span style={{ width: 1, height: 18, background: C.hair }} />
        <span style={eyebrow}>Quote</span>
      </div>

      {/* Hero */}
      <p className="cqp-fade cqp-d1" style={{ ...eyebrow, marginBottom: 10 }}>Prepared for {q.contact_first_name || q.business_name}</p>
      <h1 data-testid="cqp-title" className="cqp-fade cqp-d1"
        style={{ margin: 0, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 'clamp(30px, 6vw, 46px)', lineHeight: 1.12, letterSpacing: '-0.01em', color: C.icy }}>
        A plan built for{' '}
        <span style={{ backgroundImage: C.spectrum, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{q.business_name}</span>
      </h1>
      <p className="cqp-fade cqp-d1" style={{ margin: '10px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: C.faint }}>{q.quote_number}{vu ? ` · valid until ${vu}` : ''}</p>
      {q.intro && <p data-testid="cqp-intro" className="cqp-fade cqp-d2" style={{ margin: '20px 0 0', fontFamily: 'Inter', fontSize: 17, lineHeight: 1.65, color: C.muted, maxWidth: 620 }}>{q.intro}</p>}

      {/* Vision — see the dream first */}
      {hasVision && (
        <section data-testid="cqp-vision" className="cqp-fade cqp-d2" style={{ marginTop: 46 }}>
          <p style={{ ...eyebrow, marginBottom: 8 }}>Your vision</p>
          <h2 style={{ margin: '0 0 18px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 'clamp(20px, 4vw, 26px)', color: C.icy, lineHeight: 1.25 }}>
            Here's what we'd build for <span style={{ backgroundImage: C.spectrum, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{q.business_name}</span>
          </h2>
          <div style={{ display: 'grid', gap: 14 }}>
            {(q.vision.images || []).map((id) => {
              const src = `${API}/api/cq/${token}/vision/${id}`;
              return (
                <button key={id} data-testid={`cqp-vision-img-${id}`} className="cqp-img-card" onClick={() => openLightbox(src)}
                  style={{ padding: 0, border: `1px solid ${C.hair}`, borderRadius: 18, overflow: 'hidden', cursor: 'zoom-in', background: C.surface, boxShadow: '0 14px 44px rgba(0,0,0,0.35)' }}>
                  <img src={src} alt="Concept mockup" style={{ width: '100%', display: 'block' }} />
                </button>
              );
            })}
          </div>
          {q.vision.link_url && (
            <a data-testid="cqp-vision-link" className="cqp-cta" href={q.vision.link_url} target="_blank" rel="noreferrer" onClick={() => track('link_click')}
              style={{ display: 'block', textAlign: 'center', marginTop: 16, padding: '16px 26px', borderRadius: 999, fontFamily: 'Inter', fontSize: 15.5, fontWeight: 700, letterSpacing: '0.02em', color: '#fff', textDecoration: 'none', backgroundImage: C.spectrum, boxShadow: '0 10px 34px rgba(145,86,196,0.35)' }}>
              {q.vision.link_label || 'Explore the interactive concept'} →
            </a>
          )}
        </section>
      )}

      {/* Line items */}
      <section className="cqp-fade cqp-d3" style={{ marginTop: 46 }} data-testid="cqp-items">
        <p style={{ ...eyebrow, marginBottom: 8 }}>The work</p>
        <h2 style={{ margin: '0 0 16px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 'clamp(20px, 4vw, 26px)', color: C.icy }}>What's included</h2>
        {(q.line_items || []).map((li, i) => (
          <div key={i} data-testid={`cqp-item-${i}`}
            style={{ background: C.surface, border: `1px solid ${C.hair}`, borderLeft: `3px solid ${ACCENTS[i % ACCENTS.length]}`, borderRadius: 18, padding: '19px 22px', marginBottom: 12 }}>
            <div style={{ display: 'flex', gap: 14, alignItems: 'baseline', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 18, lineHeight: 1.3, color: C.icy, flex: 1, minWidth: 200 }}>{li.title}</h3>
              <div style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 21, color: C.icy, whiteSpace: 'nowrap' }}>{money(li.amount)}</div>
            </div>
            {li.description && <p style={{ margin: '9px 0 0', fontFamily: 'Inter', fontSize: 14, lineHeight: 1.65, color: C.muted, whiteSpace: 'pre-wrap' }}>{li.description}</p>}
            <p style={{ margin: '9px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: C.faint }}>{li.qty} × {money(li.unit_price)}{li.gst ? ' + GST' : ' (no GST)'}</p>
          </div>
        ))}

        {/* Total — the money moment */}
        <div data-testid="cqp-totals" style={{ background: C.surface, border: `1px solid ${C.hair}`, borderRadius: 18, padding: '20px 24px', marginTop: 6 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Inter', fontSize: 13, color: C.faint, marginBottom: 5 }}><span>Subtotal (ex GST)</span><b style={{ color: C.muted }}>{money(t.subtotal_ex)}</b></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Inter', fontSize: 13, color: C.faint, marginBottom: 12 }}><span>GST</span><b style={{ color: C.muted }}>{money(t.gst_total)}</b></div>
          {/* Priority + pay-in-full adjustments (only when the client picks them) */}
          {tier === 'priority' && priorityAmt > 0 && (
            <div data-testid="cqp-total-priority-line" style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Inter', fontSize: 13, color: '#EB258D', marginBottom: 5 }}>
              <span>⚡ Priority delivery ({co.priority_weeks} weeks)</span>
              <b>+{money(priorityAmt)}</b>
            </div>
          )}
          {payment === 'payinfull' && discountAmt > 0 && (
            <div data-testid="cqp-total-discount-line" style={{ display: 'flex', justifyContent: 'space-between', fontFamily: 'Inter', fontSize: 13, color: '#34D1B0', marginBottom: 5 }}>
              <span>💰 Pay-in-full discount ({co.payinfull_discount_percent}%)</span>
              <b>−{money(discountAmt)}</b>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', paddingTop: 12, borderTop: `1px solid ${C.hair}` }}>
            <span style={{ ...eyebrow }}>{payment === 'payinfull' ? 'Pay in full · today' : 'Total investment'}</span>
            <span data-testid="cqp-total-final" style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 'clamp(30px, 5vw, 38px)', backgroundImage: C.spectrum, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{money(finalTotal)}</span>
          </div>
          {payment === 'payinfull' && discountAmt > 0 && (
            <p data-testid="cqp-you-save" style={{ margin: '6px 0 0', textAlign: 'right', fontFamily: 'Inter', fontSize: 12, color: '#34D1B0', fontWeight: 700 }}>
              you save {money(discountAmt)}
            </p>
          )}
        </div>

        {/* Your options — delivery tier + payment choice. Only rendered
            when MJ has enabled at least one of the toggles per quote.
            Total above updates LIVE as the client picks. */}
        {hasCO && (
          <section data-testid="cqp-your-options" className="cqp-fade cqp-d3" style={{ marginTop: 22 }}>
            <p style={{ ...eyebrow, marginBottom: 8, color: '#EB258D' }}>Your options</p>
            <h2 style={{ margin: '0 0 4px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 'clamp(19px, 3.5vw, 24px)', color: C.icy }}>Build your deal</h2>
            <p style={{ margin: '0 0 14px', fontFamily: 'Inter', fontSize: 13.5, lineHeight: 1.6, color: C.muted, maxWidth: 620 }}>Pick your delivery tier and payment choice — the total above updates live. Whatever you land on is what the invoice reflects.</p>

            {/* Delivery tier — standard is always shown; priority card only when enabled AND addon > 0 */}
            <div data-testid="cqp-delivery-tiers" style={{ display: 'grid', gridTemplateColumns: showPriorityCard ? 'repeat(auto-fit, minmax(240px, 1fr))' : '1fr', gap: 12 }}>
              {/* Standard */}
              <button data-testid="cqp-tier-standard" onClick={() => setTier('standard')}
                style={{
                  textAlign: 'left', padding: '18px 20px', borderRadius: 16, cursor: 'pointer',
                  background: tier === 'standard' ? 'rgba(52,209,176,0.10)' : C.surface,
                  border: tier === 'standard' ? '2px solid #34D1B0' : `1px solid ${C.hair}`,
                  color: C.icy, transition: 'all 220ms ease',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #34D1B0', background: tier === 'standard' ? '#34D1B0' : 'transparent' }} />
                  <span style={{ ...eyebrow, color: '#34D1B0' }}>Standard</span>
                </div>
                <div data-testid="cqp-guarantee-seal-standard" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                  borderRadius: 999, background: 'rgba(52,209,176,0.14)', border: '1px solid rgba(52,209,176,0.4)',
                }}>
                  <span style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 18, color: '#34D1B0' }}>{co.delivery_weeks || '—'} weeks</span>
                  <span style={{ width: 1, height: 16, background: 'rgba(52,209,176,0.35)' }} />
                  <span style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#34D1B0' }}>{co.guarantee_label || 'guaranteed'}</span>
                </div>
                <p style={{ margin: '10px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>Delivered within our standard build window — signed off, in writing, before we start.</p>
              </button>

              {/* Priority — shown only when MJ enabled the toggle AND set a real addon amount */}
              {showPriorityCard && (
                <button data-testid="cqp-tier-priority" onClick={() => setTier('priority')}
                  style={{
                    textAlign: 'left', padding: '18px 20px', borderRadius: 16, cursor: 'pointer',
                    background: tier === 'priority' ? 'rgba(235,37,141,0.10)' : C.surface,
                    border: tier === 'priority' ? '2px solid #EB258D' : `1px solid ${C.hair}`,
                    color: C.icy, transition: 'all 220ms ease', position: 'relative',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #EB258D', background: tier === 'priority' ? '#EB258D' : 'transparent' }} />
                    <span style={{ ...eyebrow, color: '#EB258D' }}>⚡ Priority upgrade</span>
                  </div>
                  <div data-testid="cqp-guarantee-seal-priority" style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px',
                    borderRadius: 999, background: 'rgba(235,37,141,0.14)', border: '1px solid rgba(235,37,141,0.4)',
                  }}>
                    <span style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 18, color: '#EB258D' }}>{co.priority_weeks || '—'} weeks</span>
                    <span style={{ width: 1, height: 16, background: 'rgba(235,37,141,0.35)' }} />
                    <span style={{ fontFamily: 'Inter', fontSize: 11, letterSpacing: '0.14em', textTransform: 'uppercase', fontWeight: 700, color: '#EB258D' }}>{co.guarantee_label || 'guaranteed'}</span>
                  </div>
                  <p data-testid="cqp-priority-addon" style={{ margin: '10px 0 0', fontFamily: 'Poppins, Inter', fontSize: 15.5, fontWeight: 700, color: C.icy }}>+{money(priorityAddonAvailable)}</p>
                  <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>Accelerated delivery — priority slot, moved up the queue.</p>
                  {co.priority_scarcity_enabled && co.priority_scarcity_text && (
                    <p data-testid="cqp-scarcity" style={{ margin: '8px 0 0', fontFamily: 'Inter', fontSize: 11.5, color: '#EB258D', fontWeight: 700, letterSpacing: '0.02em' }}>
                      ▸ {co.priority_scarcity_text}
                    </p>
                  )}
                </button>
              )}
            </div>

            {/* Payment choice — only when pay-in-full is enabled AND a valid discount % is set */}
            {showPayInFullCard && (
              <div data-testid="cqp-payment-choice" style={{ marginTop: 14, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
                <button data-testid="cqp-payment-standard" onClick={() => setPayment('standard')}
                  style={{
                    textAlign: 'left', padding: '16px 20px', borderRadius: 16, cursor: 'pointer',
                    background: payment === 'standard' ? 'rgba(220,240,245,0.06)' : C.surface,
                    border: payment === 'standard' ? `2px solid ${C.icy}` : `1px solid ${C.hair}`,
                    color: C.icy, transition: 'all 220ms ease',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', border: `2px solid ${C.icy}`, background: payment === 'standard' ? C.icy : 'transparent' }} />
                    <span style={{ ...eyebrow, color: C.icy }}>Standard terms</span>
                  </div>
                  <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 13, color: C.muted, lineHeight: 1.55 }}>Deposit + progress payments as per the standard terms below.</p>
                </button>
                <button data-testid="cqp-payment-payinfull" onClick={() => setPayment('payinfull')}
                  style={{
                    textAlign: 'left', padding: '16px 20px', borderRadius: 16, cursor: 'pointer',
                    background: payment === 'payinfull' ? 'rgba(52,209,176,0.10)' : C.surface,
                    border: payment === 'payinfull' ? '2px solid #34D1B0' : `1px solid ${C.hair}`,
                    color: C.icy, transition: 'all 220ms ease',
                  }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #34D1B0', background: payment === 'payinfull' ? '#34D1B0' : 'transparent' }} />
                    <span style={{ ...eyebrow, color: '#34D1B0' }}>💰 Pay in full &amp; save {pifPct}%</span>
                  </div>
                  <p data-testid="cqp-payinfull-savings" style={{ margin: 0, fontFamily: 'Poppins, Inter', fontSize: 15.5, fontWeight: 700, color: '#34D1B0' }}>You save {money(payinfullSavingsAvailable)}</p>
                  <p style={{ margin: '4px 0 0', fontFamily: 'Inter', fontSize: 12.5, color: C.muted, lineHeight: 1.55 }}>Pay 100% up-front at invoice time and the discount above applies. Total re-calculates on selection.</p>
                </button>
              </div>
            )}
          </section>
        )}
      </section>

      {/* External services — running costs paid to third parties.
          Deliberately styled DIFFERENTLY from the accept-total so the client
          understands this is informational, not what they're accepting. */}
      {(q.external_services || []).length > 0 && (
        <section data-testid="cqp-ext-section" className="cqp-fade cqp-d3" style={{ marginTop: 32 }}>
          <p style={{ ...eyebrow, marginBottom: 8, color: '#1FB6C1' }}>Running costs — paid to third parties</p>
          <h2 style={{ margin: '0 0 6px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 'clamp(19px, 3.5vw, 24px)', color: C.icy }}>What you&rsquo;ll need to run it</h2>
          <p style={{ margin: '0 0 16px', fontFamily: 'Inter', fontSize: 13.5, lineHeight: 1.6, color: C.muted, maxWidth: 620 }}>
            These are separate subscriptions the build needs to actually run — paid to the third parties, not to Kindra. Listed here so nothing is hidden.
          </p>
          <div style={{ background: 'rgba(31,182,193,0.04)', border: `1px dashed rgba(31,182,193,0.35)`, borderRadius: 18, padding: '18px 20px' }}>
            {(q.external_services || []).map((x, i) => (
              <div key={x.id || i} data-testid={`cqp-ext-${i}`} style={{ display: 'flex', gap: 16, padding: '14px 0', borderTop: i > 0 ? `1px solid ${C.hair}` : 'none', flexWrap: 'wrap', alignItems: 'baseline' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <p style={{ margin: 0, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 15.5, color: C.icy }}>
                    {x.name}
                    {!x.required && <span data-testid={`cqp-ext-optional-${i}`} style={{ marginLeft: 8, fontFamily: 'Inter', fontSize: 11, fontWeight: 500, color: C.faint, letterSpacing: '0.05em' }}>· optional</span>}
                  </p>
                  {x.purpose && <p style={{ margin: '5px 0 0', fontFamily: 'Inter', fontSize: 13, lineHeight: 1.6, color: C.muted }}>{x.purpose}</p>}
                  <p style={{ margin: '5px 0 0', fontFamily: 'Inter', fontSize: 11, color: C.faint, letterSpacing: '0.02em' }}>Billed by: {x.billed_by === 'via_kindra' ? 'Via Kindra (added to your Kindra invoice)' : 'Third party directly'}</p>
                </div>
                <div style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 18, color: '#1FB6C1', whiteSpace: 'nowrap' }}>
                  ~{money(x.est_cost_aud)}
                  <span style={{ fontFamily: 'Inter', fontSize: 12, fontWeight: 500, color: C.muted, marginLeft: 4 }}>{x.period === 'mo' ? '/mo' : x.period === 'yr' ? '/yr' : 'one-off'}</span>
                </div>
              </div>
            ))}
            {(() => {
              const et = q.external_services_totals || {};
              const parts = [];
              if (et.per_month) parts.push(`≈ ${money(et.per_month)}/mo`);
              if (et.per_year) parts.push(`≈ ${money(et.per_year)}/yr`);
              if (et.one_off) parts.push(`≈ ${money(et.one_off)} one-off`);
              if (parts.length === 0) return null;
              return (
                <div data-testid="cqp-ext-summary" style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid rgba(31,182,193,0.35)`, display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10 }}>
                  <span style={{ ...eyebrow, color: '#1FB6C1' }}>Running-costs summary (informational)</span>
                  <span style={{ fontFamily: 'Poppins, Inter', fontWeight: 700, fontSize: 15.5, color: '#1FB6C1' }}>{parts.join(' · ')}</span>
                </div>
              );
            })()}
          </div>
          <p data-testid="cqp-ext-disclaimer" style={{ margin: '10px 0 0', fontFamily: 'Inter', fontSize: 11.5, lineHeight: 1.6, color: C.faint, fontStyle: 'italic' }}>
            Estimates only — third-party pricing is set by those providers and may change.
          </p>
        </section>
      )}

      {/* Ownership note — same doctrine as the Discover flow */}
      <section className="cqp-fade cqp-d3" data-testid="cqp-ownership"
        style={{ marginTop: 24, background: 'rgba(52,209,176,0.06)', border: `1px solid ${C.hair}`, borderLeft: '3px solid rgba(52,209,176,0.55)', borderRadius: 18, padding: '19px 22px' }}>
        <p style={{ margin: '0 0 8px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: C.muted }}>How we work</p>
        <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 14.5, lineHeight: 1.65, color: C.icy }}>
          Everything we build for you, you own. No ongoing software subscriptions for what we build — and where it makes sense, we'll connect the tools you already use rather than replace them.
        </p>
      </section>

      {/* Accept moment */}
      <section className="cqp-fade cqp-d4" style={{ marginTop: 32 }}>
        {accepted ? (
          <div data-testid="cqp-accepted" style={{ background: 'rgba(52,209,176,0.10)', border: '1px solid rgba(52,209,176,0.35)', borderRadius: 20, padding: '30px 26px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, marginBottom: 6 }}>🤝</div>
            <h2 style={{ margin: 0, fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 22, color: C.icy }}>Beauty — quote accepted{q.accepted_name ? `, ${q.accepted_name.split(' ')[0]}` : name ? `, ${name.split(' ')[0]}` : ''}.</h2>
            <p style={{ margin: '10px auto 0', fontFamily: 'Inter', fontSize: 14, lineHeight: 1.65, color: C.muted, maxWidth: 460 }}>
              {pendingUntil
                ? 'Locking this in for you… Michael\u2019ll be notified in a moment. Changed your mind? You\u2019ve got a beat to undo it below.'
                : 'Michael\u2019s been notified. He\u2019ll be in touch shortly with the invoice and kick-off details. Cheers for trusting us with this.'}
            </p>
            {pendingUntil && secondsLeft > 0 && (
              <button data-testid="cqp-undo-accept" onClick={undoAccept} disabled={undoBusy}
                className="cqp-undo-pill"
                style={{
                  marginTop: 18, display: 'inline-flex', alignItems: 'center', gap: 10,
                  padding: '10px 18px', borderRadius: 999,
                  border: '1px solid rgba(220,240,245,0.24)',
                  background: 'rgba(220,240,245,0.04)', color: C.icy,
                  fontFamily: 'Inter', fontSize: 13, fontWeight: 600, cursor: undoBusy ? 'default' : 'pointer',
                  letterSpacing: '0.01em',
                }}>
                <span style={{ color: '#34D1B0' }}>✓</span>
                <span>Accepted &mdash; <u>Undo</u></span>
                <span data-testid="cqp-undo-countdown" className="cqp-undo-count" style={{
                  fontFamily: 'Inter', fontVariantNumeric: 'tabular-nums',
                  fontSize: 12, color: 'rgba(220,240,245,0.7)',
                  padding: '2px 8px', borderRadius: 999,
                  background: 'rgba(220,240,245,0.08)',
                }}>{secondsLeft}s</span>
              </button>
            )}
          </div>
        ) : expired ? (
          <div data-testid="cqp-expired" style={{ background: C.surface, border: '1px solid rgba(235,37,141,0.35)', borderRadius: 20, padding: '24px', textAlign: 'center' }}>
            <p style={{ margin: '0 0 8px', fontFamily: 'Inter', fontSize: 15, color: C.icy }}>This quote has expired &mdash; but that&rsquo;s easily fixed.</p>
            <a href={`mailto:michael@kindraai.dev?subject=Quote ${q.quote_number}`} style={{ color: '#34D1B0', fontFamily: 'Inter', fontSize: 13.5, fontWeight: 700, textDecoration: 'none' }}>Email Michael for a refresh &rarr;</a>
          </div>
        ) : (
          <div data-testid="cqp-accept-box" style={{ background: C.surface, border: `1px solid ${C.hairBright}`, borderRadius: 20, padding: '26px 24px' }}>
            {undoDone && (
              <p data-testid="cqp-undo-hint" style={{ margin: '0 0 12px', fontFamily: 'Inter', fontSize: 12.5, color: '#34D1B0', fontWeight: 600 }}>
                &#8635; Undone &mdash; you&rsquo;re back where you were. No worries at all.
              </p>
            )}
            <p style={{ margin: '0 0 4px', ...eyebrow }}>Ready to go?</p>
            <h2 style={{ margin: '0 0 16px', fontFamily: 'Poppins, Inter', fontWeight: 600, fontSize: 21, color: C.icy }}>Lock it in</h2>
            <input data-testid="cqp-accept-name" placeholder="Your full name" value={name} onChange={(e) => setName(e.target.value)}
              style={{ width: '100%', boxSizing: 'border-box', padding: '14px 16px', borderRadius: 14, border: `1px solid ${C.hairBright}`, background: 'rgba(220,240,245,0.05)', color: C.icy, fontFamily: 'Inter', fontSize: 15, outline: 'none' }} />
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', margin: '14px 0 18px', cursor: 'pointer' }}>
              <input data-testid="cqp-accept-tick" type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ accentColor: '#34D1B0', marginTop: 3, width: 15, height: 15 }} />
              <span style={{ fontFamily: 'Inter', fontSize: 13, lineHeight: 1.55, color: C.muted }}>I accept this quote for {q.business_name} ({money(finalTotal)} inc GST{tier === 'priority' ? ' · ⚡ priority' : ''}{payment === 'payinfull' ? ' · pay-in-full' : ''}) and the terms below.</span>
            </label>
            <button data-testid="cqp-accept-btn" className="cqp-accept" onClick={accept} disabled={busy || !name.trim() || !agree}
              style={{ width: '100%', padding: '17px', borderRadius: 999, border: 'none', fontFamily: 'Inter', fontSize: 16, fontWeight: 700, letterSpacing: '0.02em', color: '#fff',
                       backgroundImage: (!name.trim() || !agree) ? 'none' : C.spectrum, background: (!name.trim() || !agree) ? 'rgba(220,240,245,0.08)' : undefined,
                       cursor: (!name.trim() || !agree) ? 'default' : 'pointer',
                       boxShadow: (!name.trim() || !agree) ? 'none' : '0 10px 32px rgba(235,37,141,0.3)' }}>
              {busy ? 'Recording…' : 'Accept this quote ✓'}
            </button>
            <p style={{ margin: '16px 0 0', textAlign: 'center' }}>
              <a data-testid="cqp-questions" href={`mailto:michael@kindraai.dev?subject=Question about quote ${q.quote_number}`} style={{ fontFamily: 'Inter', fontSize: 13, color: C.faint, textDecoration: 'none' }}>Questions? <span style={{ color: '#34D1B0', fontWeight: 700 }}>Reply to Michael &rarr;</span></a>
            </p>
          </div>
        )}
      </section>

      {q.terms && <p data-testid="cqp-terms" className="cqp-fade cqp-d4" style={{ margin: '28px 0 0', fontFamily: 'Inter', fontSize: 11.5, lineHeight: 1.65, color: C.faint }}>{q.terms}</p>}
      <footer className="cqp-fade cqp-d4" style={{ marginTop: 48, paddingTop: 20, borderTop: `1px solid ${C.hair}`, textAlign: 'center' }}>
        <p style={{ margin: 0, fontFamily: 'Inter', fontSize: 12, color: C.faint }}>
          Built by <a href="https://kindraai.dev/built" target="_blank" rel="noreferrer" style={{ color: '#9156C4', fontWeight: 700, textDecoration: 'none' }}>Kindra AI</a> · Brisbane — custom AI & automation for Australian businesses
        </p>
      </footer>
    </Shell>
  );
}
